import React from 'react';
import {useTranslation} from 'react-i18next';

import type {ButtonProps, LogoSizeModifier} from '@pexip/components';
import {Icon, IconTypes, useMobileDevice} from '@pexip/components';

import {InMeetingInputControl} from '../../InputControl/InMeetingInputControl.view';
import {TestId} from '../../../../test/testIds';

export const PresentationButton: React.FC<
    ButtonProps & {
        localIsPresenting: boolean;
        tooltipPosition?: 'top' | 'right' | 'bottom' | 'left';
        iconSize: LogoSizeModifier;
        isDisabled?: boolean;
        disabledReason?: string;
        startPresentation: () => void;
        stopPresentation: () => void;
    }
> = ({
    localIsPresenting,
    tooltipPosition = 'top',
    iconSize,
    isDisabled = false,
    disabledReason,
    startPresentation,
    stopPresentation,
    ...props
}) => {
    const {t} = useTranslation();
    const isMobileDevice = useMobileDevice();

    const nextAction = localIsPresenting ? 'stop' : 'start';
    const isSharing = nextAction === 'stop';
    const presentationAriaLabel = isSharing
        ? t('media.presentation.stop-share-screen', 'Stop sharing')
        : t('media.presentation.share-screen', 'Share screen');
    const displayedAriaLabel =
        isDisabled && disabledReason ? disabledReason : presentationAriaLabel;
    return (
        <InMeetingInputControl
            onClick={() =>
                localIsPresenting ? stopPresentation() : startPresentation()
            }
            aria-label={displayedAriaLabel}
            tooltipText={displayedAriaLabel}
            tooltipPosition={tooltipPosition}
            icon={<Icon source={IconTypes.IconShare} size={iconSize} />}
            isActive={localIsPresenting}
            shouldShowTooltip={!isMobileDevice}
            data-testid={
                isSharing
                    ? TestId.ButtonStopPresentation
                    : TestId.ButtonStartPresentation
            }
            isDisabled={isDisabled}
            {...props}
        />
    );
};

export type PresentationButtonProps = React.ComponentProps<
    typeof PresentationButton
>;
